import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../redux/store";
import {
  ProviderFaq,
  ProviderFaqApiError,
  ProviderFaqState,
} from "./types/provider-faq-types";

const API_URL = process.env.REACT_APP_API_URL;

const PRODUCT_TO_PROVIDER_ENDPOINT: Record<string, string> = {
  data: "data-providers",
  airtime: "airtime-providers",
  electric: "electric-providers",
  cable: "cable-providers",
  gift_cards: "gift-cards-providers",
  dg_coins: "dg-coins-providers",
  game_cards: "game-cards-providers",
  topup: "top-ups-providers",
};

const initialState: ProviderFaqState = {
  faqs: [],
  loading: false,
  error: null,
  activeProviderKey: null,
};

export const fetchProviderFaqs = createAsyncThunk<
  { faqs: ProviderFaq[]; providerKey: string },
  { productKey: string; providerSlug: string },
  { rejectValue: ProviderFaqApiError; state: RootState }
>("providerFaqs/fetch", async ({ productKey, providerSlug }, thunkAPI) => {
  try {
    const providerEndpoint = PRODUCT_TO_PROVIDER_ENDPOINT[productKey];
    if (!providerEndpoint) {
      return thunkAPI.rejectWithValue({ message: "Unsupported product type" });
    }

    const providerKey = `${providerEndpoint}-${providerSlug}`;
    const response = await axios.get(
      `${API_URL}/${providerEndpoint}/${providerSlug}/faqs`,
    );

    return {
      faqs: response.data?.data || [],
      providerKey,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data || { message: "Failed to fetch provider FAQs" },
    );
  }
});

const providerFaqSlice = createSlice({
  name: "providerFaqs",
  initialState,
  reducers: {
    clearProviderFaqs: (state) => {
      state.faqs = [];
      state.loading = false;
      state.error = null;
      state.activeProviderKey = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProviderFaqs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProviderFaqs.fulfilled, (state, action) => {
        state.loading = false;
        state.faqs = action.payload.faqs;
        state.activeProviderKey = action.payload.providerKey;
      })
      .addCase(fetchProviderFaqs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch provider FAQs";
      });
  },
});

export const { clearProviderFaqs } = providerFaqSlice.actions;
export default providerFaqSlice.reducer;
